import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import {
  AUTH_SESSION_CHANGING_EVENT, AUTH_SESSION_INVALID_EVENT, getAuthToken, isStoredSessionTokenCurrent,
} from '../utils/auth';

const PAYMENT_METHODS = [
  ['CASH', 'Dinheiro'], ['CREDIT_CARD', 'Cartão de crédito'], ['DEBIT_CARD', 'Cartão de débito'], ['PIX', 'Pix'],
];
const currency = (value) => Number.isFinite(Number(value)) && value !== null && value !== ''
  ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const count = (value) => value !== null && value !== undefined && String(value).trim() !== ''
  && Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;
const itemName = (line) => line.item?.name || line.item?.description || 'Item do pedido';
const positive = (value) => Number(value) > 0;

// The same cryptographic key is retained when the response to a mutation is uncertain.
const mutationId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error('Não foi possível identificar esta edição. Reabra o pedido.');
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const available = (entry) => entry.available !== false;
const emptyDraft = () => ({ quantities: {}, additions: [], reason: '', method: '', timing: 'on_delivery', received: false, refundConfirmed: false, refundMethod: '' });

export default function OrderEditDialog({
  open, orderId, source, apiBaseUrl = '', onClose, onSaved, isSessionCurrent,
}) {
  const [context, setContext] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({ loading: true, busy: '', error: '', uncertain: false, stale: false, saved: false });
  const [productId, setProductId] = useState('');
  const [productQuantity, setProductQuantity] = useState(1);
  const [optionQuantities, setOptionQuantities] = useState({});
  const [observations, setObservations] = useState('');
  const activeRef = useRef(false);
  const generationRef = useRef(0);
  const requestRef = useRef(null);
  const tokenRef = useRef('');
  const sessionCheckRef = useRef(isSessionCurrent);
  const keyRef = useRef(null);
  const pendingSaveRef = useRef(null);
  sessionCheckRef.current = isSessionCurrent;
  const current = useCallback((generation = generationRef.current) => activeRef.current
    && generation === generationRef.current
    && (!sessionCheckRef.current || sessionCheckRef.current())
    && Boolean(tokenRef.current)
    && getAuthToken() === tokenRef.current
    && isStoredSessionTokenCurrent(tokenRef.current), []);
  const endpoint = `${String(apiBaseUrl).replace(/\/$/, '')}/api/orders/${encodeURIComponent(orderId || '')}`;
  const isSalon = String(context?.source || source || '').toLowerCase() === 'salao';
  const products = context?.catalog?.products || [];
  const product = products.find((entry) => String(entry.productId) === String(productId));
  const locked = status.loading || Boolean(status.busy) || status.uncertain || status.stale || status.saved || !context?.editable;

  const resetProduct = () => { setProductId(''); setProductQuantity(1); setOptionQuantities({}); setObservations(''); };
  const load = useCallback(async () => {
    requestRef.current?.abort();
    const generation = ++generationRef.current;
    const controller = new AbortController(); requestRef.current = controller;
    keyRef.current = null; pendingSaveRef.current = null;
    setContext(null); setPreview(null); setDraft(emptyDraft());
    setProductId(''); setProductQuantity(1); setOptionQuantities({}); setObservations('');
    setStatus({ loading: true, busy: '', error: '', uncertain: false, stale: false, saved: false });
    if (!current(generation)) return;
    try {
      const response = await apiFetch(`${endpoint}/edit-context?source=${encodeURIComponent(source || '')}`, {
        cache: 'no-store', signal: controller.signal,
      });
      const data = await response.json();
      if (!current(generation) || requestRef.current !== controller) return;
      if (!response.ok || data.success === false) throw new Error(data.error || 'Não foi possível carregar o pedido para edição.');
      if (!data.order || !Array.isArray(data.items) || !Number.isSafeInteger(data.revision)
        || typeof data.fingerprint !== 'string' || !data.fingerprint
        || (data.orderId && String(data.orderId) !== String(orderId))) {
        throw new Error('O pedido não pôde ser validado. Recarregue antes de editar.');
      }
      setContext(data);
      setDraft({ ...emptyDraft(), quantities: Object.fromEntries(data.items.map((line) => [line.lineKey, line.quantity])) });
      setStatus({ loading: false, busy: '', error: '', uncertain: false, stale: false, saved: false });
    } catch (error) {
      if (error.name !== 'AbortError' && current(generation)) setStatus((before) => ({ ...before, loading: false, error: error.message }));
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [current, endpoint, orderId, source]);

  useEffect(() => {
    activeRef.current = Boolean(open);
    tokenRef.current = getAuthToken();
    if (open) load();
    const invalidate = () => {
      activeRef.current = false; generationRef.current += 1; requestRef.current?.abort();
      requestRef.current = null; pendingSaveRef.current = null; keyRef.current = null;
      setContext(null); setPreview(null); setDraft(emptyDraft());
      setStatus({ loading: false, busy: '', error: 'A sessão foi alterada. Reabra o pedido na sessão atual.', uncertain: false, stale: true, saved: false });
    };
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    return () => {
      activeRef.current = false; generationRef.current += 1; requestRef.current?.abort(); requestRef.current = null;
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    };
  }, [open, load]);

  const changeDraft = (change) => {
    if (locked || !current()) return;
    setDraft((before) => ({ ...before, ...change })); setPreview(null); keyRef.current = null;
    setStatus((before) => ({ ...before, error: '' }));
  };
  const addProduct = () => {
    if (locked || !current() || !product || !available(product)) return;
    const quantity = count(productQuantity);
    if (!quantity || quantity > 99) { setStatus((before) => ({ ...before, error: 'Informe uma quantidade de 1 a 99 para a adição.' })); return; }
    const options = []; const optionLabels = [];
    for (const group of product.optionGroups || []) {
      let groupQuantity = 0;
      for (const option of group.options || []) {
        const optionQuantity = count(optionQuantities[`${group.groupId}:${option.optionId}`] || 0);
        if (optionQuantity === null || optionQuantity > 99) { setStatus((before) => ({ ...before, error: 'Informe quantidades válidas para os complementos.' })); return; }
        if (optionQuantity > 0) {
          if (!available(option)) { setStatus((before) => ({ ...before, error: 'Um complemento selecionado está indisponível.' })); return; }
          options.push({ groupId: group.groupId, optionId: option.optionId, quantity: optionQuantity });
          optionLabels.push(`${optionQuantity} × ${option.name}`);
          groupQuantity += optionQuantity;
        }
      }
      if (groupQuantity < Number(group.minQuantity || 0)
        || (Number(group.maxQuantity) > 0 && groupQuantity > Number(group.maxQuantity))) {
        setStatus((before) => ({ ...before, error: `Confira a quantidade de complementos em ${group.name}.` })); return;
      }
    }
    changeDraft({ additions: [...draft.additions, {
      productId: product.productId, quantity, observations: observations.trim(), options,
      label: product.name, optionLabels,
    }] });
    resetProduct();
  };
  const requestBody = () => {
    if (!draft.reason.trim()) throw new Error('Informe o motivo da edição.');
    const items = [];
    for (const line of context.items) {
      const rawQuantity = Number(draft.quantities[line.lineKey]);
      const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 && rawQuantity === Number(line.quantity)
        ? rawQuantity : count(draft.quantities[line.lineKey]);
      if (quantity === null || quantity > 999) throw new Error('Confira as quantidades dos itens do pedido.');
      if (quantity > 0) items.push({ lineKey: line.lineKey, quantity });
    }
    if (!items.length && !draft.additions.length) throw new Error('O pedido deve manter pelo menos um item.');
    if (draft.refundConfirmed && !draft.refundMethod) throw new Error('Escolha a forma de devolução antes de registrar o valor devolvido.');
    if (!keyRef.current) keyRef.current = mutationId();
    return {
      source: context.source || source, expectedRevision: context.revision, expectedFingerprint: context.fingerprint,
      mutationId: keyRef.current, reason: draft.reason.trim(), items,
      additions: draft.additions.map(({ label, optionLabels, ...addition }) => addition),
      ...(!isSalon && draft.method ? { payment: { method: draft.method, timing: draft.timing, received: draft.timing === 'received' && draft.received } } : {}),
      ...(!isSalon && draft.refundConfirmed ? { refund: { confirmed: true, method: draft.refundMethod } } : {}),
    };
  };
  const previewChanges = async () => {
    if (locked || !current()) return;
    let body;
    try { body = requestBody(); } catch (error) { setStatus((before) => ({ ...before, error: error.message })); return; }
    const generation = generationRef.current;
    const controller = new AbortController(); requestRef.current = controller;
    setStatus((before) => ({ ...before, busy: 'preview', error: '' })); setPreview(null);
    try {
      const response = await apiFetch(`${endpoint}/amendments/preview`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
      const data = await response.json();
      if (!current(generation) || requestRef.current !== controller) return;
      if (!response.ok || data.success === false) {
        const stale = response.status === 409 && (!data.code || ['ORDER_AMENDMENT_CONFLICT', 'ORDER_AMENDMENT_SOURCE_CHANGED',
          'ORDER_AMENDMENT_FINANCIAL_SOURCE_CHANGED', 'ORDER_AMENDMENT_TERMINAL', 'ORDER_AMENDMENT_FISCAL_LOCKED',
          'ORDER_AMENDMENT_SALON_LOCKED'].includes(data.code));
        setStatus((before) => ({ ...before, busy: '', error: data.error || 'Não foi possível recalcular o pedido.', stale })); return;
      }
      if (!data.previewDigest || !data.order || !data.financial
        || ['orderAmount', 'totalPaid', 'totalDue', 'refundPendingAmount', 'additionalPaymentAmount'].some((field) => !Number.isFinite(data.financial[field]))) {
        throw new Error('A conferência de valores está incompleta. Tente recalcular novamente.');
      }
      setPreview({ ...data, requestBody: body }); setStatus((before) => ({ ...before, busy: '' }));
    } catch (error) {
      if (error.name !== 'AbortError' && current(generation)) setStatus((before) => ({ ...before, busy: '', error: error.message || 'Não foi possível recalcular o pedido.' }));
    } finally { if (requestRef.current === controller) requestRef.current = null; }
  };
  const save = async () => {
    if (status.busy || status.saved || status.stale || !current() || !preview) return;
    if (positive(preview.financial.additionalPaymentAmount) && !isSalon) {
      if (!draft.method) { setStatus((before) => ({ ...before, error: 'Escolha a forma de pagamento da adição e recalcule o pedido.' })); return; }
      if (draft.timing === 'received' && !draft.received) {
        setStatus((before) => ({ ...before, error: 'Confirme o recebimento do valor da adição ou selecione pagamento na entrega.' })); return;
      }
    }
    const body = pendingSaveRef.current || { ...preview.requestBody, previewDigest: preview.previewDigest };
    pendingSaveRef.current = body;
    const generation = generationRef.current;
    const controller = new AbortController(); requestRef.current = controller;
    setStatus((before) => ({ ...before, busy: 'save', error: '' }));
    let committed = false;
    try {
      const response = await apiFetch(`${endpoint}/amendments`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
      const data = await response.json();
      if (!current(generation) || requestRef.current !== controller) return;
      if (!response.ok || data.success === false) {
        const uncertain = response.status >= 500 || response.status === 408;
        if (!uncertain) pendingSaveRef.current = null;
        setStatus((before) => ({ ...before, busy: '', uncertain, stale: response.status === 409,
          error: data.error || 'Não foi possível salvar a edição.' }));
        if (!uncertain) setPreview(null);
        return;
      }
      if (!data.order || !positive(data.order.amendmentRevision ?? data.revision)) throw new Error('A resposta da edição está incompleta. Tente salvar novamente para confirmar.');
      committed = true; pendingSaveRef.current = null;
      setStatus((before) => ({ ...before, busy: '', uncertain: false, saved: true }));
      if (current(generation)) await onSaved?.(data);
    } catch (error) {
      if (error.name !== 'AbortError' && current(generation)) setStatus((before) => ({ ...before, busy: '',
        uncertain: !committed, saved: committed,
        error: committed ? 'Edição salva. Não foi possível concluir a atualização da tela ou a impressão; confira o pedido e reimprima a comanda.'
          : error.message || 'A resposta não chegou. Tente salvar novamente para confirmar a mesma edição.' }));
    } finally { if (requestRef.current === controller) requestRef.current = null; }
  };
  const close = () => { requestRef.current?.abort(); activeRef.current = false; generationRef.current += 1; onClose?.(); };
  const financial = preview?.financial;
  const paymentMissing = financial && positive(financial.additionalPaymentAmount) && !isSalon
    && (!draft.method || (draft.timing === 'received' && !draft.received));

  return <Dialog open={Boolean(open)} onClose={status.busy === 'save' ? undefined : close} fullWidth maxWidth="md" aria-labelledby="order-edit-title">
    <DialogTitle id="order-edit-title">Editar pedido</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        {status.loading && <Box role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={22} />Carregando pedido…</Box>}
        {status.error && <Alert severity="error">{status.error}</Alert>}
        {status.uncertain && <Alert severity="warning">A confirmação ainda não chegou. Mantenha esta edição e tente salvar novamente para evitar duplicação, ou recarregue o pedido para conferir o que foi salvo.</Alert>}
        {status.stale && <Alert severity="warning">Recarregue o pedido antes de editar novamente. Os dados podem ter mudado.</Alert>}
        {status.saved && <Alert severity="success">Edição salva. A comanda atualizada será enviada para impressão.</Alert>}
        {context && <>
          {!context.editable && <Alert severity="warning">{context.blockedReason || 'Este pedido não pode ser editado no momento.'}</Alert>}
          {String(context.source || source).toLowerCase() === 'ifood' && <Alert severity="info">A edição atualiza o pedido operacional no Brutos. O pedido e o pagamento oficiais no iFood permanecem no contrato original.</Alert>}
          <Typography variant="subtitle1">Itens do pedido</Typography>
          <Typography variant="body2" color="text.secondary">Total atual: {currency(context.order.total?.orderAmount)}</Typography>
          {context.items.map((line, index) => <Stack key={line.lineKey} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}><Typography>{itemName(line)}</Typography>
              <Typography variant="body2" color="text.secondary">Valor original por unidade: {currency(line.unitPrice)}</Typography>
              {line.item?.observations && <Typography variant="body2">Observação: {line.item.observations}</Typography>}
              {Array.isArray(line.item?.options) && line.item.options.map((option, optionIndex) => <Typography key={optionIndex} variant="body2" color="text.secondary">{option.quantity || 1} × {option.name || 'Complemento'}</Typography>)}
            </Box>
            <TextField label={`Quantidade de ${itemName(line)} (${index + 1})`} type="number" size="small" value={draft.quantities[line.lineKey] ?? ''}
              onChange={(event) => changeDraft({ quantities: { ...draft.quantities, [line.lineKey]: event.target.value } })}
              disabled={locked} inputProps={{ min: 0, max: 999, step: 1, readOnly: !Number.isInteger(Number(line.quantity)) }} sx={{ width: { xs: '100%', sm: 160 } }} />
            <Button disabled={locked || !positive(draft.quantities[line.lineKey])} onClick={() => changeDraft({ quantities: { ...draft.quantities, [line.lineKey]: 0 } })}
              aria-label={`Excluir ${itemName(line)} (${index + 1})`}>Excluir item</Button>
          </Stack>)}
          <Divider />
          <Typography variant="subtitle1">Adicionar itens</Typography>
          {draft.additions.map((addition, index) => <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
            <Box><Typography>{addition.quantity} × {addition.label}</Typography>
              {addition.observations && <Typography variant="body2">{addition.observations}</Typography>}
              {addition.optionLabels.map((label, optionIndex) => <Typography key={optionIndex} variant="body2" color="text.secondary">{label}</Typography>)}</Box>
            <Button disabled={locked} aria-label={`Remover adição ${addition.label} (${index + 1})`}
              onClick={() => changeDraft({ additions: draft.additions.filter((_, position) => position !== index) })}>Remover adição</Button>
          </Stack>)}
          <TextField select label="Produto para adicionar" value={productId} disabled={locked} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
            onChange={(event) => { setProductId(event.target.value); setOptionQuantities({}); setObservations(''); }} fullWidth>
            <option value="">Selecione um produto</option>
            {products.map((entry) => <option key={entry.productId} value={entry.productId} disabled={!available(entry)}>{entry.name} — {currency(entry.price)}{!available(entry) ? ' (indisponível)' : ''}</option>)}
          </TextField>
          {product && <>
            <TextField label="Quantidade da adição" type="number" value={productQuantity} disabled={locked} onChange={(event) => setProductQuantity(event.target.value)} inputProps={{ min: 1, max: 99, step: 1 }} />
            {(product.optionGroups || []).map((group) => <Box key={group.groupId}>
              <Typography variant="subtitle2">{group.name}</Typography>
              <Typography variant="caption" color="text.secondary">Mínimo: {group.minQuantity || 0}{positive(group.maxQuantity) ? ` · Máximo: ${group.maxQuantity}` : ''}</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>{(group.options || []).map((option) => <TextField key={option.optionId} size="small" type="number"
                label={`${group.name}: ${option.name}`} value={optionQuantities[`${group.groupId}:${option.optionId}`] || 0} disabled={locked || !available(option)}
                helperText={available(option) ? option.contextualPrices ? 'Preço conforme a combinação; confira no recálculo.' : currency(option.price) : 'Indisponível'}
                inputProps={{ min: 0, max: 99, step: 1 }} onChange={(event) => setOptionQuantities((before) => ({ ...before, [`${group.groupId}:${option.optionId}`]: event.target.value }))} />)}</Stack>
            </Box>)}
            <TextField label="Observação da adição" value={observations} onChange={(event) => setObservations(event.target.value)} disabled={locked} inputProps={{ maxLength: 500 }} fullWidth />
          </>}
          <Button variant="outlined" onClick={addProduct} disabled={locked || !product || !available(product)}>Incluir adição</Button>
          <TextField required label="Motivo da edição" value={draft.reason} onChange={(event) => changeDraft({ reason: event.target.value })} disabled={locked} multiline minRows={2} inputProps={{ maxLength: 1000 }} fullWidth />
          {isSalon ? <Alert severity="info">Os itens e valores serão atualizados na comanda da mesa. O pagamento continua no fechamento do Salão.</Alert> : <>
            <Typography variant="subtitle1">Pagamento do valor adicional</Typography>
            <TextField select label="Forma de pagamento da adição" value={draft.method} onChange={(event) => changeDraft({ method: event.target.value })} disabled={locked} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} fullWidth>
              <option value="">Selecione se houver valor adicional</option>{PAYMENT_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
            </TextField>
            <TextField select label="Quando receber o adicional" value={draft.timing} onChange={(event) => changeDraft({ timing: event.target.value, received: false })} disabled={locked} SelectProps={{ native: true }} fullWidth>
              <option value="on_delivery">Cobrar na entrega / retirada</option><option value="received">Já recebido pela loja</option>
            </TextField>
            {draft.timing === 'received' && <FormControlLabel control={<Checkbox checked={draft.received} disabled={locked} onChange={(event) => changeDraft({ received: event.target.checked })} />}
              label="Conferi e confirmo que a loja recebeu o valor adicional" />}
            <Typography variant="body2" color="text.secondary">O pagamento original é preservado. A adição só será marcada como paga após confirmar seu recebimento; a seleção não cria cobrança automática.</Typography>
          </>}
          {financial && <Box aria-label="Conferência de valores" sx={{ display: 'grid', gap: 0.5 }}>
            <Typography variant="h6">Conferência de valores</Typography>
            <Typography>Total atualizado: {currency(financial.orderAmount)}</Typography>
            <Typography>Total pago: {currency(financial.totalPaid)}</Typography>
            <Typography>Saldo a receber: {currency(financial.totalDue)}</Typography>
            <Typography>Adicional desta edição: {currency(financial.additionalPaymentAmount)}</Typography>
            {positive(financial.refundPendingAmount) && <Alert severity="warning">Valor a devolver: {currency(financial.refundPendingAmount)}. O valor ficará pendente até a loja confirmar a devolução manual. Esta edição não devolve valores automaticamente.</Alert>}
            <Typography variant="body2" color="text.secondary">Valores calculados pelo servidor. Confira antes de salvar; a edição salva solicita automaticamente a nova comanda.</Typography>
          </Box>}
          {!isSalon && (positive(financial?.refundPendingAmount) || draft.refundConfirmed) && <Box>
            <FormControlLabel control={<Checkbox checked={draft.refundConfirmed} disabled={locked}
              onChange={(event) => changeDraft({ refundConfirmed: event.target.checked, refundMethod: event.target.checked ? draft.refundMethod : '' })} />}
              label="Confirmei a devolução ao cliente" />
            {draft.refundConfirmed && <TextField select label="Forma de devolução" value={draft.refundMethod}
              onChange={(event) => changeDraft({ refundMethod: event.target.value })} disabled={locked} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} fullWidth>
              <option value="">Selecione a forma de devolução</option>{PAYMENT_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
            </TextField>}
            <Typography variant="body2" color="text.secondary">Marque somente depois de devolver o valor ao cliente. A confirmação registra a devolução na loja e não solicita estorno à plataforma.</Typography>
          </Box>}
        </>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
      <Button onClick={close} disabled={status.busy === 'save'}>{status.saved ? 'Fechar' : 'Cancelar'}</Button>
      {(status.error || status.stale || status.uncertain) && <Button onClick={load} disabled={Boolean(status.busy) || !current()}>Recarregar pedido e descartar edição</Button>}
      {context?.editable && <>
        <Button onClick={previewChanges} disabled={locked} variant="outlined">{status.busy === 'preview' ? 'Recalculando…' : 'Recalcular e conferir'}</Button>
        <Button onClick={save} variant="contained" disabled={!preview || Boolean(status.busy) || status.saved || status.stale || Boolean(paymentMissing)}>
          {status.busy === 'save' ? 'Salvando…' : status.uncertain ? 'Tentar salvar novamente' : 'Salvar edição e imprimir'}
        </Button>
      </>}
    </DialogActions>
  </Dialog>;
}
